import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, MessageCircle } from "lucide-react";
import { SERVICES, getServiceBySlug } from "../data/services";
import HypeHafenLogo from "../components/HypeHafenLogo";
import { useSeo } from "../hooks/useSeo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug);

  useSeo({
    title: service?.seo?.title,
    description: service?.seo?.description,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6 text-center">
        <div data-testid="service-not-found">
          <div className="hh-eyebrow mb-3">404</div>
          <h1 className="hh-headline text-3xl text-[#2E2E2E] mb-4">Leistung nicht gefunden</h1>
          <button onClick={() => navigate("/")} className="hh-btn-dark mt-4" data-testid="service-not-found-back">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </button>
        </div>
      </div>
    );
  }

  const Icon = service.icon;
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <div className="min-h-screen bg-white text-[#2E2E2E]" data-testid={`service-detail-${service.slug}`}>
      {/* Slim header */}
      <header className="bg-[#2E2E2E] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="service-detail-logo"
            className="flex items-center"
            aria-label="Zur Startseite"
          >
            <HypeHafenLogo height={56} />
          </button>
          <button
            onClick={() => navigate("/#leistungen")}
            data-testid="service-detail-back"
            className="inline-flex items-center gap-2 font-body text-sm text-white/80 hover:text-[#FFF44F] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Leistungen
          </button>
        </div>
      </header>

      {/* Hero strip */}
      <section className="bg-[#FBFBF9] border-b border-[#ECECEC]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-2xl bg-[#FFF44F] flex items-center justify-center shrink-0">
              <Icon className="w-7 h-7 text-[#2E2E2E]" strokeWidth={2.2} />
            </div>
            <div>
              <div className="hh-eyebrow mb-3">{service.detail.eyebrow}</div>
              <h1 className="hh-headline text-[2.2rem] sm:text-[2.8rem] lg:text-[3.4rem] text-[#2E2E2E] leading-[1.05]">
                {service.detail.headline}
              </h1>
            </div>
          </div>
          <p className="font-body text-[#2E2E2E]/75 text-base sm:text-lg leading-relaxed mt-8 max-w-2xl">
            {service.detail.sub}
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        {/* For whom */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
          <div className="lg:col-span-4">
            <div className="hh-eyebrow mb-3">Für wen</div>
            <h2 className="font-display text-[#2E2E2E] text-2xl sm:text-3xl leading-tight">Passt zu dir, wenn …</h2>
          </div>
          <div className="lg:col-span-8">
            <p className="font-body text-[#2E2E2E]/85 text-base sm:text-lg leading-relaxed">
              {service.detail.forWhom}
            </p>
            <ul className="mt-6 space-y-3">
              {service.detail.idealIf.map((it, i) => (
                <li key={i} data-testid={`detail-ideal-${i}`} className="flex items-start gap-3 font-body text-[#2E2E2E]/85">
                  <span className="w-5 h-5 rounded-full bg-[#FFF44F] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-[#2E2E2E]" strokeWidth={3} />
                  </span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-12">
          {service.detail.sections.map((sec, idx) => (
            <div key={sec.title} data-testid={`detail-section-${idx}`} className="grid lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4">
                <div className="font-display text-[0.72rem] tracking-[0.22em] uppercase text-[#2185D0] mb-2">
                  Abschnitt 0{idx + 1}
                </div>
                <h3 className="font-display text-[#2E2E2E] text-2xl sm:text-3xl leading-tight">{sec.title}</h3>
              </div>
              <div className="lg:col-span-8">
                <ul className="space-y-3">
                  {sec.items.map((it, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 font-body text-[#2E2E2E]/85 text-[0.97rem] leading-[1.65] pb-3 border-b border-[#ECECEC] last:border-b-0 last:pb-0"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2185D0] mt-2.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          data-testid="detail-cta-banner"
          className="mt-20 rounded-[28px] bg-[#2E2E2E] text-white px-7 py-10 lg:px-12 lg:py-14 relative overflow-hidden"
        >
          <div className="absolute -right-20 -bottom-20 w-64 h-64 rounded-full bg-[#2185D0] opacity-20" aria-hidden />
          <div className="absolute -left-8 -top-8 w-28 h-28 rounded-full border-2 border-[#FFF44F]/25" aria-hidden />

          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="font-display text-[0.72rem] tracking-[0.22em] uppercase text-[#FFF44F] mb-3">
                Bereit?
              </div>
              <h3 className="font-display text-2xl sm:text-3xl leading-tight">
                Lass uns über dein Projekt sprechen.
              </h3>
              <p className="font-body text-white/70 text-sm mt-3 leading-relaxed max-w-md">
                Kostenloses Erstgespräch (30 Min), ehrliche Ersteinschätzung deiner Kanäle und
                konkrete nächste Schritte – egal ob du buchst oder nicht.
              </p>
              <p className="font-body text-white/55 text-xs mt-3 leading-relaxed max-w-md" data-testid="detail-cta-pricing-note">
                Keine starren Pauschalen. Wir kalkulieren flexibel nach Umfang, Frequenz und Ziel – damit du nur das zahlst, was du wirklich brauchst.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
              <button
                onClick={() => navigate("/#kontakt")}
                data-testid="detail-cta-contact"
                className="hh-btn-primary justify-center"
              >
                Anfrage starten <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="https://wa.me/4915560931517"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/20 font-display text-white text-sm hover:border-[#FFF44F] hover:text-[#FFF44F] transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Per WhatsApp
              </a>
            </div>
          </div>
        </div>

        {/* Other services */}
        <div className="mt-20">
          <div className="hh-eyebrow mb-4">Weitere Leistungen</div>
          <h3 className="font-display text-[#2E2E2E] text-2xl sm:text-3xl leading-tight mb-8">
            Auch das machen wir
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherServices.map((s, i) => {
              const OtherIcon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/leistungen/${s.slug}`}
                  data-testid={`other-service-${i}`}
                  className="hh-card group flex flex-col hover:border-[#2185D0] transition-colors"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#FFF44F] flex items-center justify-center mb-4">
                    <OtherIcon className="w-5 h-5 text-[#2E2E2E]" strokeWidth={2.2} />
                  </div>
                  <h4 className="font-display text-[#2E2E2E] text-base mb-2">{s.title}</h4>
                  <p className="font-body text-[#2E2E2E]/65 text-xs leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 font-display text-xs text-[#2185D0]">
                    Mehr erfahren <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </main>

      {/* Compact footer */}
      <footer className="bg-[#2E2E2E] text-white/60 text-sm py-8 mt-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>© {new Date().getFullYear()} Hype Hafen. Aufmerksamkeit. Strategie. Wachstum.</div>
          <div className="flex gap-6 font-body">
            <Link to="/impressum" className="hover:text-[#FFF44F]">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-[#FFF44F]">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
